import { FC } from 'react'
import Typography from '@/ui/components/typography'
import { CustomCheckbox } from '@atbay/component-lib'
import {
    Section as SectionContainer,
    SectionHeader,
    OptionsList,
    OptionRow,
} from '../../FilterPopup.styles'
import { FilterSection } from '../../FilterPopup.types'
import ArrowUpIcon from '@/assets/images/arrow-up.svg?react'
import ArrowDownIcon from '@/assets/images/arrow-down.svg?react'
import { AnimatePresence } from 'framer-motion'
import {
    SectionContent,
    StyledSearchInput,
    sectionAnimationProps,
} from './Section.styles'

interface SectionProps {
    section: FilterSection
    isCollapsed: boolean
    searchValue: string
    onCollapseToggle: (sectionId: string) => void
    onSearchChange: (sectionId: string, value: string) => void
    options: FilterSection['options']
    selectedOptionIds: string[]
    onOptionToggle: (optionId: string) => void
}

const Section: FC<SectionProps> = ({
    section,
    isCollapsed,
    searchValue,
    onCollapseToggle,
    onSearchChange,
    options,
    selectedOptionIds,
    onOptionToggle,
}) => {
    return (
        <SectionContainer key={section.id}>
            <SectionHeader
                collapsed={!!isCollapsed}
                onClick={
                    section.collapsible
                        ? () => onCollapseToggle(section.id)
                        : undefined
                }
                data-testid={`section-header-${section.id}`}
            >
                <Typography
                    variant="s-light"
                    component="span"
                    className="section-title"
                >
                    {section.label}
                </Typography>
                {section.collapsible &&
                    (isCollapsed ? (
                        <ArrowDownIcon width={16} height={16} />
                    ) : (
                        <ArrowUpIcon width={16} height={16} />
                    ))}
            </SectionHeader>
            <AnimatePresence initial={false}>
                {!isCollapsed && (
                    <SectionContent
                        key="section-content"
                        {...sectionAnimationProps}
                    >
                        {section.withSearch && (
                            <StyledSearchInput
                                placeholder={`Search ${section.label.toLowerCase()}...`}
                                value={searchValue}
                                onChange={(value: string) =>
                                    onSearchChange(section.id, value)
                                }
                                dataTestId={`search-input-${section.id}`}
                                inputHeight="30px"
                                width="100%"
                            />
                        )}
                        {options.length === 0 ? (
                            <Typography variant="s-regular" component="span">
                                {`No ${section.label.toLowerCase()} found`}
                            </Typography>
                        ) : (
                            <OptionsList>
                                {options.map((option) => (
                                    <OptionRow key={option.id}>
                                        <CustomCheckbox
                                            checked={selectedOptionIds.includes(
                                                option.id
                                            )}
                                            onChange={(
                                                _: React.ChangeEvent<HTMLInputElement>,
                                                checked: boolean
                                            ) => {
                                                onOptionToggle(option.id)
                                            }}
                                            data-testid={`checkbox-${section.id}-${option.id}`}
                                            accessibilityId={`checkbox-${section.id}-${option.id}`}
                                        />
                                        {option.icon && (
                                            <span>{option.icon}</span>
                                        )}
                                        <Typography
                                            variant="s-regular"
                                            component="span"
                                        >
                                            {option.label}
                                        </Typography>
                                    </OptionRow>
                                ))}
                            </OptionsList>
                        )}
                    </SectionContent>
                )}
            </AnimatePresence>
        </SectionContainer>
    )
}

export default Section
